import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useFuelTypeServiceV1BatchCreateFuelTypeMutation } from "_services/modules/fuel_type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface FuelTypeItem {
  fuelTypeName: string;
  priceFuel: string;
  effectiveDate: string;
  description: string;
  status: string;
}

export interface AddFuelTypeFormValues {
  fuelTypeName: string;
  priceFuel: string;
  effectiveDate: string;
  description: string;
  status: string;
  items: FuelTypeItem[];
}

const schema = yup.object({
  fuelTypeName: yup.string().required("Fuel Type Name is required").max(100, "Max 100 characters"),
  priceFuel: yup.string().default(""),
  effectiveDate: yup.string().required("Effective Date is required"),
  description: yup.string().max(500, "Max 500 characters").default(""),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      fuelTypeName: yup.string().required(),
      priceFuel: yup.string().default(""),
      effectiveDate: yup.string().required(),
      description: yup.string().default(""),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const getTodayString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const useAddFuelTypeForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("fuelType");

  const [batchCreateFuelType] = useFuelTypeServiceV1BatchCreateFuelTypeMutation();

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddFuelTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      fuelTypeName: "",
      priceFuel: "",
      effectiveDate: "",
      description: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const effectiveDate = watch("effectiveDate");

  useEffect(() => {
    if (effectiveDate && effectiveDate !== getTodayString()) {
      setValue("status", "inactive");
    }
  }, [effectiveDate, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const formatPriceDisplay = (value: string) => {
    const num = Number(value.replace(/,/g, ""));
    if (isNaN(num)) return value;
    return num.toLocaleString("id-ID");
  };

  const handleAddToList = async () => {
    const isValid = await trigger(["fuelTypeName", "priceFuel", "effectiveDate", "status"]);
    if (!isValid) return;

    const { fuelTypeName, priceFuel, effectiveDate, description, status } = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.fuelTypeName.toLowerCase() === fuelTypeName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("fuelTypeName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const finalStatus = effectiveDate !== getTodayString() ? "inactive" : status;

    if (editingIndex !== null) {
      update(editingIndex, { fuelTypeName, priceFuel, effectiveDate, description, status: finalStatus });
      setEditingIndex(null);
    } else {
      append({ fuelTypeName, priceFuel, effectiveDate, description, status: finalStatus });
    }

    setValue("fuelTypeName", "");
    setValue("priceFuel", "");
    setValue("effectiveDate", "");
    setValue("description", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
    setValue("fuelTypeName", fields[index].fuelTypeName);
    setValue("priceFuel", fields[index].priceFuel);
    setValue("effectiveDate", fields[index].effectiveDate);
    setValue("description", fields[index].description);
    setValue("status", fields[index].status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      setValue("fuelTypeName", "");
      setValue("priceFuel", "");
      setValue("effectiveDate", "");
      setValue("description", "");
      setValue("status", "active");
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    setValue("fuelTypeName", "");
    setValue("priceFuel", "");
    setValue("effectiveDate", "");
    setValue("description", "");
    setValue("status", "active");
  };

  const handleSave = async () => {
    try {
      const payload = {
        fuel_types: fields.map((f) => ({
          fuel_type_name: f.fuelTypeName,
          price_fuel: Number(f.priceFuel.replace(/[^\d]/g, "")),
          effective_date: f.effectiveDate,
          description: f.description,
          status: f.status,
        })),
      };
      await batchCreateFuelType({ fuelTypeV1BatchCreateFuelTypeReq: payload }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    effectiveDate,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    formatPriceDisplay,
    t,
  };
};

export default useAddFuelTypeForm;
